import { defineStore } from "pinia";
import { useGA4 } from "../composables/GA4";
import ApiService from "../composables/ApiService";
import { usePriceHelper } from "../composables/PriceHelper";
import BasketService from "../composables/BasketService";
import { computed, ComputedRef, Ref, ref, watchEffect } from "vue";
import { Basket, BasketEntry, Product, ProductCrossSell } from "@daalder/types";
import {
    AddCombinationUpsellToBasketData,
    AddExpiviProductToBasketData,
    AddProductToBasketData,
    AddWizardToBasketData,
} from "../types/BasketMutationTypes";
import { useBasketEntryMutations } from "../composables/BasketEntryMutations";
import mitt, { Emitter } from "mitt";
import BeforeBasketFetchEvent from "../events/BasketStore/BeforeBasketFetchEvent";

const apiService = new ApiService();
const ga4 = useGA4();
const { exclVat } = usePriceHelper();
const basketService = new BasketService();

export const BasketStoreEventBus: Emitter<{
    beforeBasketFetch: BeforeBasketFetchEvent;
}> = mitt<{
    beforeBasketFetch: BeforeBasketFetchEvent;
}>();

/**
 * @todo: To clean up the API of this Store, the mapper functions (mapping data structures to different data structures)
 * should be moved outside of the store (to above it, or to a different composable).
 */
export const useBasketStore = defineStore("basket", () => {
    // Data
    const basket: Ref<Basket> = ref(null);
    const apiIncludes = [
        "entries.product.media.versions",
        "entries.product.stock",
        "entries.product.properties",
        "entries.product.shipping",
        "entries.product.components",
        "entries.product.group",
        "entries.product.productattributeset.producttemplate",
        "entries.combination_upsell.sub_parts.products.shipping_time",
        "entries.combination_upsell.sub_parts.products.media.versions",
        "entries.combination_upsell.sub_parts.products.components",
        "entries.wizard.sub_parts.products.shipping_time",
        "entries.wizard.sub_parts.products.media.versions",
        "entries.wizard.sub_parts.products.components",
        "customer.primaryInvoiceAddress",
        "customer.primaryDeliveryAddress",
        "shipping_method.tier",
        "pickup_point",
        "payment_method",
    ];
    const basketCrossSells: Ref<ProductCrossSell[]> = ref([]);
    const justAddedProductsData: Ref<
        (
            | AddProductToBasketData
            | AddCombinationUpsellToBasketData
            | AddWizardToBasketData
            | AddExpiviProductToBasketData
        )[]
    > = ref([]);
    const isConfirmationModalOpen: Ref<boolean> = ref(false);
    const isConfirmationModalLoadingState: Ref<boolean> = ref(false);
    const isCurrentlyMutating: Ref<boolean> = ref(false);

    // Composables
    const {
        addEntry,
        addEntries,
        removeEntry,
        updateEntry,
        addWizardEntry,
        addWizardPart,
        removeWizardPart,
        updateWizardPart,
        addCombinationUpsellEntry,
        addCombinationUpsellPart,
        removeCombinationUpsellPart,
        updateCombinationUpsellPart,
        updateBasketEntryParts,
        addExpiviEntry,
        updateExpiviEntry,
    } = useBasketEntryMutations();
    const { getPrice } = usePriceHelper();

    // Computed properties
    // PRICES AND DISCOUNTS
    const totalPrice: ComputedRef<number> = computed(() => {
        return exclVat() ? +basket.value?.total?.price_excluding_vat : +basket.value?.total?.price;
    });

    const subtotalPrice: ComputedRef<number> = computed(() => {
        return exclVat() ? +basket.value?.subtotal?.list_price_excluding_vat : +basket.value?.subtotal?.list_price;
    });

    // TODO: RENAME OR REMOVE THIS METHOD
    const advantage: ComputedRef<number> = computed(() => {
        if (basket.value) {
            return basketService.calculateAdvantage(basket.value) || 0;
        }

        return null;
    });

    const basketHasStoneProducts: ComputedRef<boolean> = computed(() => {
        return basket.value?.entries.some((entry: BasketEntry) => entry.product?.template === "stone") ?? false;
    });

    const itemCount: ComputedRef<number> = computed(() => {
        let rawItemsCount =
            basket.value?.entries.reduce((partialSum, entry) => {
                return partialSum + entry.amount;
            }, 0) ?? 0;

        return Math.round(rawItemsCount);
    });

    const confirmationModalLayoutType: ComputedRef<"simple" | "stone" | "wood" | "wizard" | "combiupsell"> = computed(
        () => {
            if (justAddedProductsData.value.length === 0) {
                return "simple";
            }

            if (justAddedProductsData.value?.[0].hasOwnProperty("upsell_id")) {
                return "combiupsell";
            }

            if (justAddedProductsData.value?.[0].hasOwnProperty("wizard_id")) {
                return "wizard";
            }

            // Get configured Product template
            let productTemplate: string =
                justAddedProductsData.value[0].product.template ??
                justAddedProductsData.value[0].product.productattributeset?.product_template?.name ??
                "simple";

            // Map some possible values
            switch (productTemplate) {
                case "wood-new":
                    productTemplate = "wood";
                    break;
                case "Default":
                    productTemplate = "simple";
                    break;
            }

            // Fallback to simple if no size variations are available
            if (productTemplate == "wood" && justAddedProductsData.value.length === 1) {
                productTemplate = "simple";
            }

            // Explicitly cast value to desired return type
            if (["simple", "stone", "wood"].includes(productTemplate) === false) {
                productTemplate = "simple";
            }

            return <"simple" | "stone" | "wood" | "wizard" | "combiupsell">productTemplate;
        },
    );

    // Methods
    // GENERIC
    async function init(): Promise<void> {
        return fetchBasket();
    }

    // HELPERS
    function getBasketEntryById(id: number): BasketEntry | null {
        return basket.value?.entries?.find((entry) => {
            return entry.id === id;
        });
    }

    // BASKET
    async function fetchBasket(): Promise<void> {
        let initialRequestParams: { name: string; value: string }[] = [
            { name: "includes", value: apiIncludes.join(",") },
        ];

        // Allow an event implementer to provide/customize URL params before performing the API call
        let event = new BeforeBasketFetchEvent(initialRequestParams);
        BasketStoreEventBus.emit("beforeBasketFetch", event);

        let requestParams: string = event.requestParams
            .map((param) => {
                return `${param.name}=${param.value}`;
            })
            .join("&");

        return apiService
            .get(`v2/basket?${requestParams}`)
            .then((response) => {
                basket.value = response.data;

                // Start a background API call to fetch the CrossSells data
                fetchCrossSells();

                return response;
            })
            .catch((error) => {
                // Do nothing (yet)
            });
    }

    /**
     * @description Because this is such a large request, it's done in the background after the main Basket fetch call
     * is done. The purpose of this data is to enrich the Basket page with CrossSells, so it's not critical to load it
     * fast.
     */
    async function fetchCrossSells(): Promise<void> {
        let crossSellsIncludes = [
            "entries.product.crossSells.cross_sell_product.media.versions",
            "entries.product.crossSells.cross_sell_product.variations.values",
            "entries.product.crossSells.cross_sell_product.group",
            "entries.product.crossSells.cross_sell_product.properties",
        ];

        apiService.get(`v2/basket?includes=${crossSellsIncludes.join(",")}`).then((response) => {
            basketCrossSells.value = response.data?.entries.flatMap((entry: BasketEntry) => entry.product.cross_sells);
        });
    }

    async function updateComment(comment: string): Promise<any> {
        basket.value.comment = comment;

        return apiService
            .put(`v2/basket?includes=${apiIncludes.join(",")}`, {
                comment: comment,
            })
            .then((response) => {
                return response;
            })
            .catch((error) => {
                throw error;
            });
    }

    async function updateCoupon(discountCode: string): Promise<any> {
        return apiService
            .post("v2/basket/coupon?includes=" + apiIncludes.join(","), {
                coupon_code: discountCode,
            })
            .then((response) => {
                basket.value = response.data;
                return response;
            })
            .catch((error) => {
                throw error;
            });
    }

    async function clearBasket() {
        return await apiService
            .delete(`v2/basket/entries?includes=${apiIncludes.join(",")}`)
            .then((response: any) => {
                basket.value = response.data;
                return response;
            })
            .catch((error) => {
                throw error;
            });
    }

    async function getBasketSharingToken() {
        return await apiService
            .post(`v2/basket/share`)
            .then((response: any) => {
                return response.data?.share_token;
            })
            .catch((error) => {
                throw error;
            });
    }

    async function checkSharedBasketIsValid(sharedBasketToken: string): Promise<boolean> {
        return await apiService
            .get(`v2/basket/share/${sharedBasketToken}`)
            .then((response: any) => {
                return !!response.data?.entries?.length;
            })
            .catch((error) => {
                return false;
            });
    }

    async function overwriteBasketWithSharedBasket(sharedBasketToken: string) {
        return await apiService
            .post(`v2/basket/share/${sharedBasketToken}/apply?includes=${apiIncludes.join(",")}`)
            .then((response: any) => {
                basket.value = response.data;
                return response;
            })
            .catch((error) => {
                throw error;
            });
    }

    function updateDeliveryMethod() {
        const options: {
            shipping_method_id?: number;
            pickup_point_id?: number;
        } = {};

        if (basket.value?.shipping_method?.id) {
            options["shipping_method_id"] = basket.value.shipping_method.id;
        }

        if (basket.value?.pickup_point?.id) {
            options["pickup_point_id"] = basket.value.pickup_point.id;
        }

        return apiService
            .put(`v2/basket?includes=${apiIncludes.join(",")}`, options)
            .then((response: any) => {
                basket.value = response.data;

                // Prepare GA4 data
                let mappedGAProducts = ga4.mapBasketEntriesToGAProductsData(basket.value.entries);
                let shippingTierName = basket.value.shipping_method?.tier?.name ?? null;

                // Track custom GA4 Shipping Method event
                ga4.trackGAAddShippingInfo(mappedGAProducts, shippingTierName, basket.value.discount_code?.code ?? "");

                return response;
            })
            .catch((error) => {
                throw error;
            });
    }

    function updatePaymentMethod() {
        let issuerCode = basket.value.payment_method?.issuer?.id.toString() ?? "";

        return apiService
            .put(`v2/basket?includes=${apiIncludes.join(",")}`, {
                payment_method_id: basket.value.payment_method.id,
                issuer_code: issuerCode,
            })
            .then((response) => {
                basket.value = response.data;

                // Prepare GA4 data
                let mappedGAProducts = ga4.mapBasketEntriesToGAProductsData(basket.value.entries);
                let shippingTierName = basket.value.shipping_method?.tier?.name;
                let paymentFee = basket.value.payment_method?.transaction_fee?.price ?? 0;
                let paymentMethodName = basket.value.payment_method?.name;

                // Add the issuer to the Payment Method name in the GA4 event
                if (basket.value.payment_method?.issuer) {
                    paymentMethodName += " - " + basket.value.payment_method.issuer.name;
                }

                // Track custom GA4 Payment Method event
                ga4.trackGAAddPaymentInfo(
                    mappedGAProducts,
                    shippingTierName,
                    paymentMethodName,
                    +paymentFee,
                    basket.value.discount_code?.code ?? "",
                );

                return response;
            })
            .catch((error) => {
                throw error;
            });
    }

    return {
        // Data
        itemCount,
        basket,
        apiIncludes,
        justAddedProductsData,
        isConfirmationModalOpen,
        isConfirmationModalLoadingState,
        totalPrice,
        subtotalPrice,
        advantage,
        basketHasStoneProducts,
        confirmationModalLayoutType,
        isCurrentlyMutating,
        basketCrossSells,

        // Helper functions
        getBasketEntryById,

        // Entry mutation
        addEntry,
        addEntries,
        removeEntry,
        updateEntry,

        // CombiUpsell Entry mutation
        addCombinationUpsellEntry,
        removeCombinationUpsellPart,
        updateCombinationUpsellPart,

        // Wizard Entry mutation
        addWizardEntry,
        removeWizardPart,
        updateWizardPart,

        // Coupon mutation
        updateCoupon,

        // Checkout info mutation
        updateDeliveryMethod,
        updatePaymentMethod,
        updateComment,

        // Expivi Entry mutation
        addExpiviEntry,
        updateExpiviEntry,

        // Misc
        init,
        fetchBasket,
        clearBasket,
        getBasketSharingToken,
        checkSharedBasketIsValid,
        overwriteBasketWithSharedBasket,
    };
});
